import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import AnnounceIcon from '@/assets/announce.svg';
import { breadcrumbTitleKeys, useBreadcrumbsContext } from '@/breadcrumbs';
import { TextInput } from '@/components/form/text-input';
import ConfirmAnnouncementModal from '@/components/modals/confirm-announcement';
import ConfirmResetModal from '@/components/modals/confirm-reset';
import ConfirmResponseNotificationModal from '@/components/modals/confirm-response-notification';
import PlaceCallModal from '@/components/modals/place-call';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import {
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@/components/ui/tabs';
import { strings } from '@/constants/strings';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useCurrentUnit } from '@/hooks/use-current-unit';
import { useOpenState } from '@/hooks/use-open-state';
import { usePolicies } from '@/hooks/use-policies';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { SendAnnouncementForm } from '@/pages/authenticated/devices/drawer-views/send-announcement-form';
import { SendNotificationForm } from '@/pages/authenticated/devices/drawer-views/send-notification-form';
import { api } from '@/redux/api';
import { UnitSettingsDropdown } from './drawer-views/unit-settings-dropdown';
import { AddressBookPanel } from './panels/address-book/address-book';
import { CallsPanel } from './panels/calls/calls-panel';
import { ConfigurationPanel } from './panels/configuration/configuration-panel';
import { ConnectivityPanel } from './panels/connectivity/connectivity-panel';
import {
  NotificationResponsesPanel,
  WellbeingResponsesPanel,
} from './panels/wellbeing-responses/responses-panel';

type DrawerView = 'announce' | 'edit' | 'responseNotification';
type DrawerContext = { view: DrawerView; unitId?: number };
type AnnouncementConfirmationContext = {
  unitIds: number[];
  notificationText: string;
};

type ResponseNotificationConfirmationContext = {
  unitIds: number[];
  notificationText: string;
};

type ResetConfirmationContext = {
  unitId: number;
  organisationId: number;
};

type CallConfirmationContext = {
  unitId: number;
};

type EditUnitFormInputs = {
  unitName: string;
};

export const UnitPage: FC = () => {
  const { setTitles: setBreadcrumbTitles } = useBreadcrumbsContext();
  const { data: unit } = useCurrentUnit();
  const { data: policies } = usePolicies();
  const { organisation } = useCurrentOrganisation();
  const [updateUnit] = api.useUpdateUnitMutation();

  const unitName = unit?.unitName ?? '';
  const unitId = unit?.id;
  const hasDevice = !!unit?.devices?.length;

  /**
   * Drawer that holds the announcment and edit forms
   */
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'announce', unitId });

  /**
   * Edit form for the UnitId (name)
   */
  const {
    register: registerEditForm,
    handleSubmit: handleEditSubmit,
    formState: { isSubmitting: isEditSubmitting },
    reset: resetEditForm,
  } = useForm<EditUnitFormInputs>({
    defaultValues: { unitName: unitName },
  });

  /**
   * Announcement confirmation modal - check with the user before sending an
   * announcement
   */
  const {
    state: announcementConfirmationState,
    open: openAnnouncementConfirmation,
    toggleOpen: toggleAnnouncementConfirmationOpen,
  } = useOpenState<AnnouncementConfirmationContext>({
    unitIds: [],
    notificationText: '',
  });

  /**
   * Announcement confirmation modal - check with the user before sending an
   * announcement
   */
  const {
    state: responseNotificationConfirmationState,
    open: openResponseNotificationConfirmation,
    toggleOpen: toggleResponseNotificationConfirmationOpen,
  } = useOpenState<ResponseNotificationConfirmationContext>({
    unitIds: [],
    notificationText: '',
  });

  /**
   * Reset confirmation modal - check with the user before resetting a
   * unit
   */
  const {
    state: resetConfirmationState,
    open: openResetConfirmation,
    toggleOpen: toggleResetConfirmationOpen,
  } = useOpenState<ResetConfirmationContext>({
    unitId: unit!.id,
    organisationId: organisation!.id,
  });

  /**
   * Call confirmation modal - check with the user before placing a call
   */
  const {
    state: callConfirmationState,
    open: openCallConfirmation,
    toggleOpen: toggleCallConfirmationOpen,
  } = useOpenState<CallConfirmationContext>({
    unitId: unit!.id,
  });

  /**
   * When opening the drawer, reset the form to the current unit name,
   * so that if they edit, close and re-open, it goes back to the correct version
   */
  useEffect(() => {
    if (drawerState.open) {
      if (drawerState.context.view === 'edit') {
        resetEditForm({ unitName: unitName });
      }
    }
  }, [drawerState.context.view, drawerState.open, resetEditForm, unitName]);

  // TODO: this could all go in a hook which sets up keys based on current unit and current organisation
  useEffect(() => {
    setBreadcrumbTitles((titles) => ({
      ...titles,
      [breadcrumbTitleKeys.currentGroupName]: unit?.group?.groupName ?? '',
      [breadcrumbTitleKeys.currentUnitName]: unitName,
    }));
    return () => {
      setBreadcrumbTitles((titles) => ({
        ...titles,
        [breadcrumbTitleKeys.currentGroupName]: '',
        [breadcrumbTitleKeys.currentUnitName]: '',
      }));
    };
  }, [unitName, setBreadcrumbTitles, unit?.group?.groupName]);

  const handleEditUnitSubmit = async (data: EditUnitFormInputs) => {
    if (unitId && unit) {
      await updateUnit({
        id: unitId,
        unitName: data.unitName,
        organisation: unit.organisation,
        // Include other required fields that shouldn't change
        groupId: unit.group?.id,
      });
      toggleDrawerOpen(false);
    }
  };

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'announce': {
        const { unitId } = drawerState.context;
        if (unitId) {
          return (
            <SendAnnouncementForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openAnnouncementConfirmation({
                  unitIds: [unitId],
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      case 'responseNotification': {
        const { unitId } = drawerState.context;
        if (unitId) {
          return (
            <SendNotificationForm
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                openResponseNotificationConfirmation({
                  unitIds: [unitId],
                  notificationText: data.notificationText,
                });
              }}
            />
          );
        }
        break;
      }
      case 'edit': {
        return (
          <form
            className="flex flex-col gap-4 p-4"
            onSubmit={handleEditSubmit(handleEditUnitSubmit)}
          >
            <div>
              <label
                className="mb-2 block text-sm font-medium"
                htmlFor="unitName"
              >
                Unit Name (ID)
              </label>
              <TextInput
                {...registerEditForm('unitName', { required: true })}
                disabled={isEditSubmitting}
                id="unitName"
                placeholder="Enter unit name"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                disabled={isEditSubmitting}
                onClick={() => toggleDrawerOpen(false)}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button disabled={isEditSubmitting} type="submit">
                Save Changes
              </Button>
            </div>
          </form>
        );
      }
      default:
        break;
    }

    // If no view has returned anything, close the drawer
    if (drawerState.open) {
      toggleDrawerOpen(false);
    }
    return null;
  };

  if (!unitId) {
    return null;
  }

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup subtitlePrefix={unitName} title={strings.unit.title} />
        <div className="flex items-center justify-end gap-2">
          <Tooltip anchorSelect="#announceButtonTooltip">
            {hasDevice
              ? 'Send a message without asking for a response'
              : 'No device added to the unit. Please add a device to send announcements.'}
          </Tooltip>
          <a id="announceButtonTooltip">
            <Button
              disabled={!unitId || !hasDevice}
              onClick={() => openDrawer({ view: 'announce', unitId })}
              variant="secondary"
            >
              <AnnounceIcon className="h-6" /> Announcement
            </Button>
          </a>
          <Tooltip anchorSelect="#responseNotificationButtonTooltip">
            {hasDevice
              ? 'Send a message, and log a yes/no response'
              : 'No device added to the unit. Please add a device to send notifications.'}
          </Tooltip>
          <a id="responseNotificationButtonTooltip">
            <Button
              disabled={!unitId || !hasDevice}
              onClick={() =>
                openDrawer({ view: 'responseNotification', unitId })
              }
              variant="primary"
            >
              <AnnounceIcon className="h-6" /> Response Notification
            </Button>
          </a>
          <Tooltip anchorSelect="#callButtonTooltip" place="top">
            {hasDevice
              ? 'Open a window where you can place a call to the devices in the room'
              : 'No device added to the unit. Please add a device to send calls.'}
          </Tooltip>
          <a id="callButtonTooltip">
            <Button
              disabled={!unitId || !hasDevice}
              onClick={() => openCallConfirmation({ unitId: unitId! })}
              variant="primary"
            >
              <AnnounceIcon className="h-6" /> Place Call
            </Button>
          </a>
          <UnitSettingsDropdown
            disabled={!unitId}
            onEdit={() => openDrawer({ view: 'edit', unitId })}
            onReset={() => openResetConfirmation()}
          />
        </div>
      </HeadingGroupContainerLayout>
      <TabGroup>
        <TabList>
          <Tab>Configuration</Tab>
          <Tooltip anchorSelect="#wellbeingTooltip">
            Click here to see responses to wellbeing checks
          </Tooltip>
          <a id="wellbeingTooltip">
            <Tab>Wellbeing</Tab>
          </a>
          <Tooltip anchorSelect="#notificationsTooltip">
            Click here to see responses to notifications
          </Tooltip>
          <a id="notificationsTooltip">
            <Tab>Notifications</Tab>
          </a>
          {policies?.addressBook.editEnabled && (
            <>
              <Tooltip anchorSelect="#addressBookTooltip">
                Click here to manage phone numbers in a unit
              </Tooltip>
              <a id="addressBookTooltip">
                <Tab>Address Book</Tab>
              </a>{' '}
            </>
          )}
          <Tab>Connectivity</Tab>
          <Tab>Calls</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ConfigurationPanel />
          </TabPanel>
          <TabPanel>
            <WellbeingResponsesPanel />
          </TabPanel>
          <TabPanel>
            <NotificationResponsesPanel />
          </TabPanel>
          {policies?.addressBook.editEnabled && (
            <TabPanel>
              <AddressBookPanel />
            </TabPanel>
          )}
          <TabPanel>
            <ConnectivityPanel />
          </TabPanel>
          <TabPanel>
            <CallsPanel />
          </TabPanel>
        </TabPanels>
      </TabGroup>

      <Drawer
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
        title={
          drawerState.context.view === 'edit'
            ? 'Edit Unit'
            : drawerState.context.view === 'announce'
              ? 'Announcement'
              : 'Response Notification'
        }
      >
        {renderDrawerContent()}
      </Drawer>

      <ConfirmAnnouncementModal
        isOpen={announcementConfirmationState.open}
        notificationText={
          announcementConfirmationState.context.notificationText
        }
        onCancel={() => toggleAnnouncementConfirmationOpen(false)}
        onSend={() => {
          toggleAnnouncementConfirmationOpen(false);
          toggleDrawerOpen(false);
        }}
        unitIds={announcementConfirmationState.context.unitIds}
      />
      <ConfirmResponseNotificationModal
        isOpen={responseNotificationConfirmationState.open}
        notificationText={
          responseNotificationConfirmationState.context.notificationText
        }
        onCancel={() => toggleResponseNotificationConfirmationOpen(false)}
        onSend={() => {
          toggleResponseNotificationConfirmationOpen(false);
          toggleDrawerOpen(false);
        }}
        unitIds={responseNotificationConfirmationState.context.unitIds}
      />
      <ConfirmResetModal
        isOpen={resetConfirmationState.open}
        onCancel={() => toggleResetConfirmationOpen(false)}
        onSend={() => {
          toggleResetConfirmationOpen(false);
        }}
        organisationId={resetConfirmationState.context.organisationId}
        unitId={resetConfirmationState.context.unitId}
      />

      <PlaceCallModal
        open={callConfirmationState.open}
        setOpen={() => toggleCallConfirmationOpen(false)}
        unitId={unitId}
      />
    </>
  );
};
